import { z } from 'zod';
import { ClaimReasonEnum } from '../../enums/gsp.js';
import { helperSchema } from '../../zodExtensions.js';
import { orderAddress } from './address.js';
import { claim } from './claim.js';
import { claimReason } from './claimReasons.js';
import { storeOrderLineItem } from './lineItem.js';
import { Pegasus } from './pegasus.js';
import { shippingClaimCustomerUi } from './shippingClaim/shippingClaim.js';
import { orderTracker } from './tracking.js';
// ----------- this file contains types related to two types of orders GSP + CREW, determined by what type of claim they will ultimately create ---------------
const eligibility = z.object({
    lastEligibleDate: z.string().nullish(),
    isEligible: z.boolean(),
});
export const crewOrderLineItem = storeOrderLineItem.merge(z.object({
    // the exchangeLineItemId representing a modification to the original order's line items
    exchangeLineItemIdFromPlatform: z.string().optional(),
    ineligibleLineItemFootnote: helperSchema.optionalizedString,
    automationMadeIneligibleOn: z.string().optional(),
    productTagsAtPurchase: z.array(z.string()).optional(),
    eligibilityStoreRuleId: z.number().optional(),
    return: z.object({
        claimReasons: z.array(claimReason),
        isFinalSale: z.boolean(),
        isExchangeOnly: z.boolean(),
        exchange: eligibility,
        giftCard: eligibility,
        refund: eligibility,
    }),
    warranty: eligibility.extend({
        claimReasons: z.array(claimReason),
        isIneligibleWarrantyClaimAllowed: z.boolean().optional(),
        ineligibleWarrantyClaimTitle: z.string().optional(),
        ineligibleWarrantyClaimDisclaimer: z.string().optional(),
    }),
}));
export const trackerWithLineItems = orderTracker.merge(z.object({ lineItemsInShipment: crewOrderLineItem.array() }));
export const orderFulfillment = z.object({
    id: z.number(),
    isFulfilled: z.boolean(),
    destinationAddress: orderAddress.nullable(),
    tracker: trackerWithLineItems.optional(),
});
const shippingClaimReasonOverride = z.object({
    reason: z.nativeEnum(ClaimReasonEnum),
    headline: z.string(),
    description: z.string(),
    buttonText: z.string(),
});
// order to create a GSP claim
export const storeOrder = z.object({
    storeId: z.number(),
    storeSlug: z.string(),
    canceledOn: z.string().optional(),
    storeName: z.string(),
    countryCode: z.string().optional(),
    storefrontId: z.string(),
    isGiftReturn: z.boolean().optional(),
    id: z.number(),
    idFromPlatform: z.string(),
    isExchangeOfExchange: z.boolean().optional(),
    orderNo: z.string(),
    email: z.string(),
    currencyCode: z.string(),
    lineItems: z.array(storeOrderLineItem).min(1),
    shippingAddress: orderAddress.nullable(),
    billingAddress: orderAddress.nullable(),
    wasShippingProtected: z.boolean(),
    isUnprotectedShippingClaimPermitted: z.boolean(),
    shippingTitle: z.string().optional(),
    shippingPrice: z.string().optional(),
    claims: z.array(claim).optional(), // crewClaims
    shippingClaims: z.array(shippingClaimCustomerUi).optional(), // gspClaims
    shippingClaimReasons: z.array(z.enum(ClaimReasonEnum)),
    shippingClaimReasonOverrides: z.array(shippingClaimReasonOverride).optional(),
    customerCurrencyConversionRate: z.number().optional(),
    platformAdminUrl: z.string().optional(),
    wasCreatedFromRegistration: z.boolean(),
    /** The order is a replacement Corso created to resolve a shipping claim. */
    wasCreatedFromReorder: z.boolean().optional(),
    isShippingClaimImgRequired: z.boolean(),
    createdOn: z.string(),
});
// order to create a CREW claim
export const crewOrder = storeOrder.omit({ lineItems: true }).merge(z.object({
    globalEOrderId: z.string().optional(),
    startGiftReturnUrl: z.string().optional(),
    // when every return and warranty window started counting, per the store's window start event
    windowStartsOn: z.string().optional(),
    returnUntil: z.string().optional(),
    warrantyUntil: z.string().optional(),
    editUntil: z.string().optional(),
    isOrderEditable: z.boolean().optional(),
    isOrderFulfilled: z.boolean(),
    fulfillments: orderFulfillment.array(),
    lineItems: z.array(crewOrderLineItem).min(1),
    offerInstantExchange: Pegasus.eventOfferInstantExchangeParams.optional(),
    allowClaimTypeSelection: Pegasus.eventAllowClaimTypeSelection.optional(),
    rightToWithdraw: Pegasus.eventRightToWithdraw.shape.params.optional(),
}));
const orderListItem = crewOrder
    .pick({
    id: true,
    orderNo: true,
    idFromPlatform: true,
    createdOn: true,
})
    .merge(z.object({
    customerEmail: z.string(),
    customerName: z.string().nullable(),
    storeId: z.number(),
    storeName: z.string(),
}));
export const orderListSchema = z.array(orderListItem);
//# sourceMappingURL=order.js.map